import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import CursorClickIcon from '../../ui/icons/CursorClickIcon'
import DownloadIcon from '../../ui/icons/DownloadIcon'
import SlidersIcon from '../../ui/icons/SlidersIcon'
import XmarkIcon from '../../ui/icons/XmarkIcon'
import useIssueExport from '../hooks/useIssueExport'
import { pluralize } from '../../../utils/textHelpers'

// Above the cookie banner (z-index 98, theme/cookies.scss), which would otherwise cover the actions.
const GROUP_CLASS = 'content-fixed-center tw:fixed tw:bottom-10 tw:left-1/2 tw:-translate-x-1/2 tw:z-99 '
  + 'tw:inline-flex tw:w-max tw:whitespace-nowrap tw:items-center tw:pl-4 tw:pr-2 tw:py-2.5 '
  + 'tw:bg-gray-800 '
  + 'tw:rounded-lg! tw:border tw:border-solid tw:border-gray-300 '
  + 'tw:shadow-[0_0_8px_0_rgba(0,0,0,0.04)]'
const COUNT_CLASS = 'tw:flex tw:items-center tw:text-sm tw:font-medium '
  + 'tw:leading-normal tw:text-white'
const ACTION_CLASS = 'tw:inline-flex tw:items-center tw:justify-center tw:gap-1 '
  + 'tw:min-h-9 tw:min-w-9 tw:px-3 tw:py-2 tw:rounded! tw:bg-transparent tw:border-0 '
  + 'tw:text-sm tw:leading-5! tw:font-medium tw:text-white tw:cursor-pointer '
  + 'tw:hover:bg-white/10 tw:active:bg-gray-900 '
  + 'tw:disabled:opacity-50 tw:disabled:cursor-not-allowed'
const SEPARATOR_CLASS = 'tw:w-px tw:h-5 tw:mx-2 tw:bg-white tw:shrink-0'
const CLEAR_CLASS = 'tw:inline-flex tw:items-center tw:justify-center '
  + 'tw:h-9 tw:px-2 tw:rounded! tw:bg-transparent tw:border-0 tw:text-white '
  + 'tw:cursor-pointer tw:hover:bg-white/10 tw:active:bg-gray-900'
const ICON_CLASS = 'tw:size-4.5 tw:shrink-0'
// The download glyph fills its whole viewBox, unlike the others: inset it inside the 18px frame.
const DOWNLOAD_ICON_CLASS = `${ICON_CLASS} tw:p-0.5`

const EMPTY_SELECTION = new Set()

const SelectionToolbar = ({
  buildExportUrl, toastContainerId, selectedIds, onClear, onSelectAllFiltered,
  totalCount = 0, allFiltered = false, onChangeStatus = null
}) => {
  const { exportTo, exporting } = useIssueExport(toastContainerId)
  const count = allFiltered ? totalCount : selectedIds.size
  const label = pluralize(count, 'sélection')

  const exportExcel = useCallback(() => {
    exportTo(buildExportUrl('xlsx', selectedIds))
  }, [exportTo, buildExportUrl, selectedIds])

  const exportPdf = useCallback(() => {
    exportTo(buildExportUrl('pdf', selectedIds))
  }, [exportTo, buildExportUrl, selectedIds])

  return (
    <div aria-label="Actions sur la sélection" className={GROUP_CLASS} role="toolbar">
      <span className={COUNT_CLASS}>{label}</span>
      <span aria-hidden="true" className={SEPARATOR_CLASS} />
      <button className={ACTION_CLASS} type="button" onClick={onSelectAllFiltered}>
        <CursorClickIcon className={ICON_CLASS} />
        Tout sélectionner
      </button>
      {onChangeStatus ? (
        <button className={ACTION_CLASS} type="button" onClick={onChangeStatus}>
          <SlidersIcon className={ICON_CLASS} />
          Changer le statut
        </button>
      ) : null}
      <button className={ACTION_CLASS} disabled={exporting} type="button" onClick={exportExcel}>
        <DownloadIcon className={DOWNLOAD_ICON_CLASS} />
        Exporter Excel
      </button>
      <button className={ACTION_CLASS} disabled={exporting} type="button" onClick={exportPdf}>
        <DownloadIcon className={DOWNLOAD_ICON_CLASS} />
        Exporter PDF
      </button>
      <span aria-hidden="true" className={SEPARATOR_CLASS} />
      <button
        aria-label="Annuler la sélection"
        className={CLEAR_CLASS}
        type="button"
        onClick={onClear}
      >
        <XmarkIcon className={ICON_CLASS} />
      </button>
    </div>
  )
}

SelectionToolbar.propTypes = {
  allFiltered: PropTypes.bool,
  buildExportUrl: PropTypes.func.isRequired,
  onChangeStatus: PropTypes.func,
  onClear: PropTypes.func.isRequired,
  onSelectAllFiltered: PropTypes.func.isRequired,
  selectedIds: PropTypes.instanceOf(Set),
  toastContainerId: PropTypes.string.isRequired,
  totalCount: PropTypes.number
}

SelectionToolbar.defaultProps = {
  allFiltered: false,
  onChangeStatus: null,
  selectedIds: EMPTY_SELECTION,
  totalCount: 0
}

export default SelectionToolbar
